import type { FoliarBoard, FoliarBoardRound } from '../../services/nutritionService';
import { currentMonthKey } from '../../utils/monthKey';

export interface FoliarBoardKpi {
  roundsInFlight: number;
  roundsPlanned: number;
  roundsInProgress: number;
  awaitingSignoff: number;
  oldestAwaitingDays: number | null;
  appliedThisMonth: number;
  /**
   * Sessions with status 'draft'. Named for what it measures: `draft` implies
   * "no combo assigned", but the converse is FALSE — nutritionSessionRollup.js
   * checks deletedAt / signedOffAt / markedIncompleteAt / cancelledAt BEFORE the
   * combos-empty branch, so a combo-less session that was marked incomplete
   * reports 'incomplete'. Calling this "unassigned" would over-promise.
   * Mirrors soil's `SoilBoardKpi.draftSessions`.
   */
  draftSessions: number;
  roundsWithDrafts: number;
}

export interface FoliarBoardAttention {
  awaitingSignoff: { roundId: string; name: string; count: number }[];
  drafts: { roundId: string; name: string; count: number }[];
  uncreated: { roundId: string; name: string; orchardCount: number }[];
  /**
   * #537: rounds carrying products AND orchards but no plannedStart — both
   * conjuncts required, matching the server rule this mirrors
   * (`nutritionRoundValidation.plannedStartMissingInRound`) and the sibling
   * predicates in `foliarRoundEditor.ts` and `undatedRoundsReport.js`. Stock
   * is allocated in plannedStart order with nulls LAST, so these absorb the
   * shortfall however urgent they are. Listed here because this is the
   * screen where a manager can fix the cause; a migration log is not a
   * prompt.
   *
   * A round with no products, or with products but no orchards, is excluded —
   * it is not yet configured, so a missing date is not yet a problem. An
   * orchard-less round also produces no demand record at all
   * (`nutritionOrderModel.js` drops it before the schedule is built), so it
   * cannot be "ordered last" — the strip's own heading — because it cannot be
   * ordered at all.
   */
  undated: { roundId: string; name: string; orchardCount: number }[];
}

export type RoundState = 'notCreated' | 'planned' | 'inProgress' | 'complete';

const DAY_MS = 86_400_000;

/**
 * Board order. `month` is 1-12 and a season crosses a year boundary, so month
 * alone would sort January before August and read as the previous season.
 * plannedStart wins; rounds without one follow, by month; a remaining tie keeps
 * the programme's own array order (Array.prototype.sort is stable in ES2019+).
 */
export function orderRounds(rounds: FoliarBoardRound[]): FoliarBoardRound[] {
  return [...rounds].sort((a, b) => {
    const aStart = a.plannedStart ? Date.parse(a.plannedStart) : Number.MAX_SAFE_INTEGER;
    const bStart = b.plannedStart ? Date.parse(b.plannedStart) : Number.MAX_SAFE_INTEGER;
    if (aStart !== bStart) return aStart - bStart;
    return (a.month ?? 13) - (b.month ?? 13);
  });
}

/**
 * The ONE classifier for a round's state. Exported because both the KPI counts
 * below and FoliarBoardPage's status cell (Task 7) consume it — two functions
 * previously disagreed on the completed-but-unsigned case and put an "In
 * progress" pill above a delta reading "0 in progress, 1 planned".
 *
 * Completed-but-unsigned work has been DONE, not merely planned: it reads as
 * in progress until sign-off closes the round.
 */
export function roundState(r: FoliarBoardRound): RoundState {
  const s = r.sessions;
  if (s.total === 0) return 'notCreated';
  if (s.signedOff === s.total) return 'complete';
  if (s.inProgress > 0 || s.completed > 0) return 'inProgress';
  // #452: an attempted-and-failed round has outstanding work. Without this the
  // bare `return 'planned'` below caught it as a fallthrough, presenting failed
  // work as not-yet-started and putting it in the roundsPlanned BUCKET when it
  // belongs in roundsInProgress. (It also appeared in roundsInFlight — but so
  // does every planned round: IN_FLIGHT deliberately unions 'planned' and
  // 'inProgress'. That union is correct; the bucket was the defect.)
  // Unreachable while nutrition writes no 'incomplete' status (#486); the
  // fallthrough is the defect, not the input.
  if (s.incomplete > 0) return 'inProgress';
  return 'planned';
}

/** A round is "in flight" when work has been planned or started but not finished. */
const IN_FLIGHT: RoundState[] = ['planned', 'inProgress'];

export function summariseFoliarBoard(
  board: FoliarBoard,
  now: Date = new Date(),
): { kpi: FoliarBoardKpi; attention: FoliarBoardAttention } {
  const rounds = board.rounds || [];
  const thisMonth = currentMonthKey(now);

  const states = rounds.map(roundState);
  const oldestIso = rounds
    .map((r) => r.sessions.oldestAwaitingCompletedAt)
    .filter((v): v is string => !!v)
    .sort()[0];

  const withDrafts = rounds.filter((r) => r.sessions.draft > 0);

  const kpi: FoliarBoardKpi = {
    roundsInFlight: states.filter((s) => IN_FLIGHT.includes(s)).length,
    roundsPlanned: states.filter((s) => s === 'planned').length,
    roundsInProgress: states.filter((s) => s === 'inProgress').length,
    awaitingSignoff: rounds.reduce((s, r) => s + r.sessions.awaitingSignoff, 0),
    // Clamped at zero: a completedAt in the future must not read as negative days.
    oldestAwaitingDays: oldestIso
      ? Math.max(0, Math.floor((now.getTime() - new Date(oldestIso).getTime()) / DAY_MS))
      : null,
    // Bucketing happens HERE, on raw server timestamps, using the LOCAL month
    // rule. The server deliberately does not pre-bucket — it would have to pick
    // a timezone, and a UTC bucket loses a completion made in the first two
    // hours of a SAST month.
    appliedThisMonth: rounds.reduce(
      (sum, r) => sum + (r.sessions.completedAts || [])
        .filter((iso) => currentMonthKey(new Date(iso)) === thisMonth).length,
      0,
    ),
    draftSessions: withDrafts.reduce((s, r) => s + r.sessions.draft, 0),
    roundsWithDrafts: withDrafts.length,
  };

  return {
    kpi,
    attention: {
      awaitingSignoff: rounds
        .filter((r) => r.sessions.awaitingSignoff > 0)
        .map((r) => ({ roundId: r.roundId, name: r.name, count: r.sessions.awaitingSignoff })),
      drafts: withDrafts
        .map((r) => ({ roundId: r.roundId, name: r.name, count: r.sessions.draft })),
      // Uncreated only when the round has orchards to convert AND no session at
      // all. A partially converted round is already visible on the board, and a
      // round with no orchards has nothing to create.
      uncreated: rounds
        .filter((r) => r.sessions.total === 0 && r.orchardCount > 0)
        .map((r) => ({ roundId: r.roundId, name: r.name, orchardCount: r.orchardCount })),
      undated: rounds
        .filter((r) => !r.plannedStart && (r.productNames || []).length > 0 && r.orchardCount > 0)
        .map((r) => ({ roundId: r.roundId, name: r.name, orchardCount: r.orchardCount })),
    },
  };
}
